<template>
  <!-- Shared bottom-right navigation/action links for the public-page shells
       (AuthSplitLayout footer + PublicPageShell footer). The link styling is
       passed in via `linkClass` so the SAME markup can sit in split layouts
       and theme-adaptive flat pages. -->
  <nav
    class="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs"
    aria-label="Public navigation"
  >
    <router-link
      v-for="link in visibleLinks"
      :key="link.name"
      :to="link.to"
      :class="link.action ? actionClass : linkClass"
    >
      {{ t(link.label) }}
    </router-link>

    <button
      type="button"
      :class="linkClass"
      @click="openNewsletter"
    >
      {{ t('footer.newsletter') }}
    </button>
  </nav>
</template>

<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useDisplayStore } from '@/stores/useDisplayStore';

  const props = defineProps({
    /** Route names to omit (e.g. the current page). */
    exclude: { type: Array, default: () => [] },
    /** Append a "Login" action link, styled with `actionClass`. */
    showLogin: { type: Boolean, default: false },
    /** Styling for the muted navigation links. */
    linkClass: {
      type: String,
      default: 'text-theme-text-muted transition-colors hover:text-theme-text-secondary',
    },
    /** Styling for the emphasised action link (Login). */
    actionClass: {
      type: String,
      default: 'font-medium text-theme-palette-emerald transition-colors hover:text-theme-palette-emerald',
    },
  });

  const { t } = useI18n();
  const displayStore = useDisplayStore();
  const openNewsletter = () => displayStore.newsletterModalToggle(true);

  const allLinks = computed(() => {
    const links = [
      { name: 'subscriptions', to: '/subscriptions', label: 'menu_subscriptions' },
      { name: 'api-docs', to: '/api-docs', label: 'menu_api_docs' },
      { name: 'about', to: '/about', label: 'menu_about_us' },
      { name: 'privacy-statement', to: '/privacy-statement', label: 'privacy_policy' },
    ];
    if (props.showLogin) {
      links.push({ name: 'login', to: '/login', label: 'menu_login', action: true });
    }
    return links;
  });

  const visibleLinks = computed(() =>
    allLinks.value.filter((link) => !props.exclude.includes(link.name))
  );
</script>
